// Gişe sekmelerinin rastgele "Aw, Snap" (Error code 5) ile kapanmasının nedenini görmek için bellek izi tutar.
// - Her sayfa örneği dakikada bir küçük bir anlık görüntüyü localStorage'a yazar (heap, DOM, canlı WebGL, store boyutu, rota).
// - Örnek düzgün kapanınca (pagehide) kaydı "closed" olur. Kapanmadan kaybolan örnek bir sonraki açılışta
//   "beklenmedik kapanma" olarak çökme kayıtlarına taşınır: aynı sekme yeniden yüklenirse hemen, başka sekmede
//   STALE_AFTER_MS sonra. Tarayıcı ya da bilgisayar kapanması da bu sınıfa düşebilir.
// - Destek ekibi konsolda __b2bMemoryReport() ile kayıtları görür. Rota yalnız path olarak tutulur (query yok).
// Not: Chrome performance.memory değerlerini kaba aralıklarla ve en fazla 20 dakikada bir günceller.

const HEARTBEAT_MS = 60000;
const STALE_AFTER_MS = 5 * 60000;
const MAX_SNAPSHOTS = 30;
const MAX_UNEXPECTED_EXITS = 10;
const RECORD_PREFIX = "__memTelemetry:";
const EXIT_LOG_KEY = "__memUnexpectedExits";
const TAB_INSTANCE_KEY = "__memTelemetryInstance";

function readJson(storage, key) {
  try {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (e) {
    return null;
  }
}

function writeJson(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Ölçüm uygulamayı bozmasın (kota vb.).
  }
}

function storageKB(storage, key) {
  try {
    return Math.round(((storage.getItem(key) || "").length * 2) / 1024);
  } catch (e) {
    return null;
  }
}

// Oluşturulan WebGL bağlamlarını izler; kapatılmayan Pixi örnekleri burada birikir.
function trackWebglContexts() {
  const seen = new WeakSet();
  const live = [];
  const originalGetContext = HTMLCanvasElement.prototype.getContext;
  HTMLCanvasElement.prototype.getContext = function (type, ...args) {
    const context = originalGetContext.call(this, type, ...args);
    if (context && /webgl/i.test(type) && !seen.has(context)) {
      seen.add(context);
      if (typeof WeakRef === "function") live.push(new WeakRef(context));
    }
    return context;
  };
  return () => {
    for (let i = live.length - 1; i >= 0; i--) {
      const context = live[i].deref();
      if (!context || context.isContextLost()) live.splice(i, 1);
    }
    return live.length;
  };
}

function takeSnapshot(getRoute, countWebgl) {
  const memory = performance.memory;
  return {
    t: new Date().toISOString(),
    upMin: Math.round(performance.now() / 60000),
    route: getRoute(),
    heapMB: memory ? Math.round(memory.usedJSHeapSize / 1048576) : null,
    domNodes: document.getElementsByTagName("*").length,
    webgl: countWebgl(),
    vuexKB: storageKB(localStorage, "vuex"),
    vuexTabKB: storageKB(sessionStorage, "vuex-tab"),
  };
}

function summarize(exit) {
  const snapshots = exit.snapshots || [];
  const last = snapshots[snapshots.length - 1] || {};
  const heaps = snapshots.map((s) => s.heapMB).filter((v) => v !== null && v !== undefined);
  return {
    detectedAt: exit.detectedAt,
    startedAt: exit.startedAt,
    sameTab: exit.sameTab,
    upMin: last.upMin,
    route: last.route,
    heapMB: last.heapMB,
    peakHeapMB: heaps.length ? Math.max(...heaps) : null,
    heapLimitMB: exit.heapLimitMB,
    domNodes: last.domNodes,
    webgl: last.webgl,
    vuexKB: last.vuexKB,
  };
}

// Önceki örneklerin kayıtlarını tarar; kapanmadan kaybolanları beklenmedik kapanma kaydına taşır.
function collectUnexpectedExits(previousTabInstanceId, now) {
  const exits = readJson(localStorage, EXIT_LOG_KEY) || [];
  const found = [];
  let keys = [];
  try {
    keys = Object.keys(localStorage).filter((key) => key.startsWith(RECORD_PREFIX));
  } catch (e) {
    return found;
  }
  keys.forEach((key) => {
    const instanceId = key.slice(RECORD_PREFIX.length);
    const record = readJson(localStorage, key);
    const sameTab = instanceId === previousTabInstanceId;
    const stale = !record || now - record.lastSeen > STALE_AFTER_MS;
    if (sameTab && document.wasDiscarded) {
      // Bellek tasarrufu için atılan sekme çökme sayılmaz; başka sekme bunu çökme diye yazdıysa geri al.
      const index = exits.findIndex((exit) => exit.instanceId === instanceId);
      if (index >= 0) exits.splice(index, 1);
      localStorage.removeItem(key);
      return;
    }
    if (!record || record.state === "closed" || (record.state === "frozen" && (sameTab || stale))) {
      localStorage.removeItem(key);
      return;
    }
    if (record.state === "alive" && (sameTab || stale)) {
      const exit = { ...record, instanceId, sameTab, detectedAt: new Date(now).toISOString() };
      exits.push(exit);
      found.push(exit);
      localStorage.removeItem(key);
    }
    // Diğer durumlar: başka bir sekmede hâlâ çalışan örnek; dokunma.
  });
  writeJson(localStorage, EXIT_LOG_KEY, exits.slice(-MAX_UNEXPECTED_EXITS));
  return found;
}

export function installMemoryTelemetry({ getRoute }) {
  if (window.__b2bMemoryTelemetryInstalled) return;
  window.__b2bMemoryTelemetryInstalled = true;

  const countWebgl = trackWebglContexts();
  let previousTabInstanceId = null;
  try {
    previousTabInstanceId = sessionStorage.getItem(TAB_INSTANCE_KEY);
  } catch (e) {
    // sessionStorage yoksa yalnız başka sekmeden tespit çalışır.
  }

  const found = collectUnexpectedExits(previousTabInstanceId, Date.now());
  if (found.length) {
    console.warn("Önceki B2B sayfası beklenmedik kapandı (ayrıntı: __b2bMemoryReport()):", found.map(summarize));
  }

  const instanceId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  const recordKey = RECORD_PREFIX + instanceId;
  try {
    sessionStorage.setItem(TAB_INSTANCE_KEY, instanceId);
  } catch (e) {
    // yok say
  }
  const record = {
    startedAt: new Date().toISOString(),
    lastSeen: Date.now(),
    state: "alive",
    heapLimitMB: performance.memory ? Math.round(performance.memory.jsHeapSizeLimit / 1048576) : null,
    snapshots: [],
  };
  const save = () => writeJson(localStorage, recordKey, record);
  const heartbeat = () => {
    record.lastSeen = Date.now();
    record.state = "alive";
    record.snapshots.push(takeSnapshot(getRoute, countWebgl));
    if (record.snapshots.length > MAX_SNAPSHOTS) record.snapshots.shift();
    save();
  };
  const setState = (state) => {
    record.state = state;
    record.lastSeen = Date.now();
    save();
  };

  heartbeat();
  setInterval(heartbeat, HEARTBEAT_MS);
  window.addEventListener("pagehide", () => setState("closed"));
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) setState("alive");
  });
  document.addEventListener("freeze", () => setState("frozen"));
  document.addEventListener("resume", () => setState("alive"));

  window.__b2bMemoryReport = () => {
    const exits = readJson(localStorage, EXIT_LOG_KEY) || [];
    console.table(exits.map(summarize));
    return { unexpectedExits: exits, current: record };
  };
}
